import { AccelFile, Entity, School } from '..';
import { observable, action, computed } from 'mobx';
import moment, { type Moment } from 'moment';
import { ISerializable } from '../Entity';
import {
    parseScenarioTransferApplicationSettings,
    type ScenarioTransferApplicationSettings,
} from './Settings/ScenarioTransferApplicationSettings';
import { resolveSurfaceMap, type SurfaceMap } from './SurfaceContents';

export enum ApplicationType {
    Custom = 'custom',
    Telegram = 'telegramGroups',
    VkGroups = 'vkGroups',
    MaxGroups = 'maxGroups',
    MetaConversions = 'metaConversions',
    YandexMetrika = 'yandexMetrika',
    GoogleAnalytics = 'googleAnalytics',
    CustomCabinet = 'customCabinet',
    ScenarioTransfer = 'scenarioTransfer',
    Marketplace = 'marketplace'
}

export {
    parseScenarioTransferApplicationSettings,
};
export type { ScenarioTransferApplicationSettings };

export type ApplicationMeta = {
    stableVersion?: string | null;
    betaVersion?: string | null;
    sourceIconUrl?: string | null;
    surfaceContents?: Record<string, unknown> | null;
    betaSurfaceContents?: Record<string, unknown> | null;
};

export const applicationSurfaceContents = (
    meta: ApplicationMeta | null | undefined,
    beta: boolean,
): Record<string, unknown> | null =>
    (beta && meta?.betaSurfaceContents ? meta.betaSurfaceContents : meta?.surfaceContents) ?? null;

export const parseApplicationMeta = (metaJson: string | null | undefined): ApplicationMeta | null => {
    if (!metaJson) return null;
    try {
        return JSON.parse(metaJson) as ApplicationMeta;
    } catch {
        return null;
    }
};

export type ApplicationCapabilityHandler = {
    type?: string;
    surface?: string;
};

export type ApplicationCapability = {
    actions?: Record<string, { handler?: ApplicationCapabilityHandler }>;
};

export type ApplicationProvidesMap = Record<string, ApplicationCapability>;

export const parseApplicationProvides = (settingsJson: string | null | undefined): ApplicationProvidesMap | null => {
    if (!settingsJson) return null;
    try {
        return (JSON.parse(settingsJson) as { provides?: ApplicationProvidesMap }).provides || null;
    } catch {
        return null;
    }
};

export type ApplicationPricing = {
    monthlyAmount?: number;
    oneTimeAmount?: number;
    trialDays?: number;
    trialEndsAt?: string;
    currency?: string;
    author?: string;
};

export const hasAnyPositiveValue = (v: unknown): boolean => {
    if (v == null) return false;
    if (typeof v === 'number') return v > 0;
    if (typeof v === 'object') return Object.values(v as Record<string, unknown>).some(hasAnyPositiveValue);
    return false;
};

export const isPaidPricing = (pricing: ApplicationPricing | null | undefined): boolean => {
    if (!pricing) return false;
    return hasAnyPositiveValue(pricing.monthlyAmount) || hasAnyPositiveValue(pricing.oneTimeAmount);
};

export const parseApplicationPricing = (pricingJson: string | null | undefined): ApplicationPricing | null => {
    if (!pricingJson) return null;
    try {
        return JSON.parse(pricingJson) as ApplicationPricing;
    } catch {
        return null;
    }
};

export const isTrialActive = (pricing: ApplicationPricing | null | undefined): boolean => {
    if (!pricing?.trialEndsAt) return false;
    return new Date() < new Date(pricing.trialEndsAt);
};

export const trialDaysLeft = (pricing: ApplicationPricing | null | undefined): number | null => {
    if (!pricing?.trialEndsAt) return null;
    const end = new Date(pricing.trialEndsAt).getTime();
    const ms = end - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export const isTrialExpired = (pricing: ApplicationPricing | null | undefined): boolean => {
    if (!pricing?.trialEndsAt) return false;
    return new Date() >= new Date(pricing.trialEndsAt);
}

export const isApplicationPaid = (app: Application): boolean =>
    isPaidPricing(parseApplicationPricing(app.pricingJson));

export const isApplicationSubscription = (app: Application): boolean =>
    hasAnyPositiveValue(parseApplicationPricing(app.pricingJson)?.monthlyAmount);

export const isApplicationOneTime = (app: Application): boolean => {
    const p = parseApplicationPricing(app.pricingJson);
    return !hasAnyPositiveValue(p?.monthlyAmount) && hasAnyPositiveValue(p?.oneTimeAmount);
}

export const isExternalStoreApplication = (app: Application | { type?: ApplicationType } | null | undefined): boolean =>
    app?.type === ApplicationType.ScenarioTransfer;

export const getExternalStoreApplicationSettings = (app: Application | null | undefined): ScenarioTransferApplicationSettings | null => {
    if (!isExternalStoreApplication(app)) return null;
    return parseScenarioTransferApplicationSettings(app?.settingsJson);
};

export default class Application extends Entity implements ISerializable {
    constructor(application?: Partial<Application>) {
        super(application);
        if (application) this.update(application);
    }

    @observable title: string;
    @observable description: string;
    @observable shortDescription: string;
    @observable pricingJson: string;
    @observable settingsJson: string | null;
    @observable createdDate: Moment;
    @observable updatedDate: Moment | null;

    @observable type: ApplicationType;
    @observable slug: string | null;
    @observable status: number;
    @observable image: AccelFile | null;
    @observable school: School | null;
    @observable schoolAppsCount: number;
    @observable enabled: boolean;
    @observable hasStable: boolean;
    @observable hasBeta: boolean;
    @observable betaDescription: string | null;
    @observable isSingleInstanceFlag: boolean;
    @observable metaJson: string | null;
    @observable betaSettingsJson: string | null;

    @computed get isSingleInstance() {
        return this.isSingleInstanceFlag === true || this.type === ApplicationType.CustomCabinet;
    }

    @computed get isMarketplace(): boolean {
        return this.type === ApplicationType.Marketplace;
    }

    @computed get meta(): ApplicationMeta | null {
        return parseApplicationMeta(this.metaJson);
    }

    @computed get marketplaceStableVersion(): string | null {
        return this.meta?.stableVersion ?? null;
    }

    @computed get marketplaceBetaVersion(): string | null {
        return this.meta?.betaVersion ?? null;
    }

    @computed get marketplaceIconUrl(): string | null {
        return this.meta?.sourceIconUrl ?? null;
    }

    @computed get pricing(): ApplicationPricing | null {
        return parseApplicationPricing(this.pricingJson);
    }

    @computed get provides(): ApplicationProvidesMap | null {
        return parseApplicationProvides(this.settingsJson);
    }

    @computed get surfaceContents(): SurfaceMap {
        return resolveSurfaceMap(this.meta?.surfaceContents);
    }

    surfaceContentsFor(beta: boolean): SurfaceMap {
        return resolveSurfaceMap(applicationSurfaceContents(this.meta, beta));
    }

    @action update(application: Partial<Application>, allowUndefined = false) {
        super.update(application, allowUndefined);
    }

    toJson() {
        return {
            ...this,
            image: this.image?.toJson(),
        };
    }

    static fromJson(json: any): Application {
        return new Application({
            id: json.id,
            title: json.title,
            description: json.description,
            shortDescription: json.shortDescription,
            type: json.type,
            pricingJson: json.pricingJson,
            settingsJson: json.settingsJson ?? null,
            softDeleted: json.softDeleted,
            createdDate: json.createdDate ? moment(json.createdDate) : undefined,
            updatedDate: json.updatedDate ? moment(json.updatedDate) : undefined,
            image: json.image ? AccelFile.fromJson(json.image) : null,
            school: json.school ? School.fromJson(json.school) : null,
            schoolAppsCount: json.schoolLinksCount ?? 0,
            enabled: json.enabled ?? true,
            slug: json.slug ?? null,
            status: typeof json.status === 'number' ? json.status : 0,
            hasStable: json.hasStable ?? true,
            hasBeta: json.hasBeta ?? false,
            betaDescription: json.betaDescription ?? null,
            isSingleInstanceFlag: json.isSingleInstance ?? false,
            metaJson: json.metaJson ?? null,
            betaSettingsJson: json.betaSettingsJson ?? null,
        });
    }
}
